import axiosInstance from "@app/axiosInstance";
import { T_Report } from "@app/entities/report/types";
import { useCompany } from "@app/providers/CompanyContextProvider";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import notification from "antd/lib/notification";

import axios from "axios";

export const useReportMutations = () => {
  const { selectedCompany } = useCompany();
  const queryClient = useQueryClient();
  const companyId = selectedCompany?.companyId;

  const { isPending: isBroadcastReportPending, mutate: broadcastReport } =
    useMutation({
      mutationFn: (reportId: number) =>
        axiosInstance.put(`/admin/${companyId}/report/broadcast`, {
          reportId,
        }),
      onSuccess: () => {
        notification.success({
          description: "Report has been broadcast",
          title: "Success",
        });
        queryClient.invalidateQueries({
          predicate: (query) =>
            query.queryKey.includes("reports") ||
            query.queryKey.includes("companies"),
        });
      },
      onError: (error) => {
        console.error(error);
        if (axios.isAxiosError(error)) {
          if (error.response?.status === 444) {
            notification.warning({
              title: "No members found to alert.",
            });
          }
        }
      },
    });

  const { mutate: updateReport, isPending: isUpdateReportPending } =
    useMutation({
      mutationFn: (payload: T_Report & {file: File | null}) =>
        {
          const formData = new FormData();

            for (const key in payload) {
              if (Object.prototype.hasOwnProperty.call(payload, key)) {
                if (key === "file" && payload.file) {
                  formData.append("image", payload.file);
                } else {
                  formData.append(key, (payload as any)[key]);
                }
              }
            }

          return axiosInstance.put(`/admin/${companyId}/report`, formData, {
            headers: { "Content-Type": "multipart/form-data" }
          });
        },
      onSuccess: () => {
        notification.success({
          description: "Report has been updated",
          title: "Success",
        });
        queryClient.invalidateQueries({
          predicate: (query) => query.queryKey.includes("reports"),
        });
      },
    });

  const { mutate: markReportBad, isPending: isMarkReportBadPending } =
    useMutation({
      mutationFn: ({
        reportId,
        reason,
      }: {
        reportId: number;
        reason: string;
      }) =>
        axiosInstance.put(`/admin/${companyId}/report/bad`, {
          reportId,
          reason,
        }),
      onSuccess: () => {
        notification.success({
          description: "Report has been marked as bad",
          title: "Success",
        });
        queryClient.invalidateQueries({
          predicate: (query) => query.queryKey.includes("reports"),
        });
      },
      onError: (error) => {
        console.error(error);
        notification.error({ title:"Failed to mark report as bad" });
      },
    });

  const { mutate: deleteReport, isPending: isDeleteReportPending } =
    useMutation({
      mutationFn: (reportId: number) =>
        axiosInstance.delete(`/admin/${companyId}/report`, {
          // Stupid, but this is how Axios handles payloads for DELETE
          data: { reportId },
        }),
      onSuccess: () => {
        notification.success({
          description: "Report has been deleted",
          title: "Success",
        });
        queryClient.invalidateQueries({
          predicate: (query) => query.queryKey.includes("reports"),
        });
      },
      onError: (error) => {
        console.error(error);
        notification.error({ title:"Failed to delete report" });
      },
    });

  const { isPending: isResetReportPending, mutate: resetReport } = useMutation({
    mutationFn: ({
      reportId,
      shouldAlert = true,
    }: {
      reportId: number;
      shouldAlert?: boolean;
    }) =>
      axiosInstance.put(`/admin/${companyId}/report/reset`, {
        reportId,
        shouldAlert,
      }),
    onSuccess: () => {
      notification.success({
        description: "Report has been reset",
        title: "Success",
      });
      queryClient.invalidateQueries({
        predicate: (query) =>
          query.queryKey.includes("reports") ||
          query.queryKey.includes("companies"),
      });
    },
    onError: (error) => {
      console.error(error);
      notification.error({ title: "Failed to reset report" });
    },
  });

  const isMutationPending =
    isBroadcastReportPending ||
    isResetReportPending ||
    isUpdateReportPending ||
    isMarkReportBadPending ||
    isDeleteReportPending;
  return {
    isMutationPending,
    resetReport,
    broadcastReport,
    updateReport,
    markReportBad,
    deleteReport,
  };
};
